import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  ShoppingCartCheckout as ReorderIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrders } from '../hooks/useOrders';
import { useInvoice } from '../hooks/useInvoice';
import { useApiCart } from '../hooks/useApiCartProvider';
import { Order } from '../services';
import { computeBreakdown, formatIDR } from '../utils/pricing';
import ReorderDialog from './orders/ReorderDialog';
import { Snackbar } from '@mui/material';

const getStatusColor = (status: Order['status']): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'DRAFT': return 'warning';
    case 'SUBMITTED': return 'primary';
    case 'CONFIRMED': return 'info';
    case 'SHIPPED': return 'secondary';
    case 'DELIVERED': return 'success';
    case 'INVOICED': return 'success';
    case 'CLOSED': return 'default';
    case 'CANCELLED': return 'error';
    default: return 'default';
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export interface OrderListProps {
  /** Restrict the list to one order source. When set:
   *   - 'portal' renders only portal-placed orders (default behavior)
   *   - 'epicor' renders only offline orders (sales-rep created in
   *     Epicor) and applies read-only treatment — Reorder/Cancel/Edit
   *     buttons are hidden.
   *  Omit to show both. */
  source?: 'portal' | 'epicor';
}

export const OrderList: React.FC<OrderListProps> = ({ source }) => {
  const {
    orders,
    currentOrder,
    isLoading,
    error,
    total,
    hasMore,
    loadOrders,
    loadMoreOrders,
    loadOrderById,
    clearError,
  } = useOrders();

  const isOfflineTab = source === 'epicor';

  // Order Details Dialog uses a card layout on narrow viewports and the
  // 5-column table on tablet+. The 5-column table doesn't fit the
  // ~360-390px mobile viewport; product/description would scroll off
  // the left edge and users saw "antity | Unit Price | Total".
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [statusFilter, setStatusFilter] = useState<Order['status'] | 'all'>('all');
  const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);

  // Reorder ("Buy Again") dialog state.
  const [reorderTarget, setReorderTarget] = useState<Order | null>(null);
  const [reorderToast, setReorderToast] = useState<string>('');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { totalItems: cartItemCount, loadCart } = useApiCart();

  const {
    isLoading: invoiceLoading,
    isGenerating,
    infoMessage: invoiceInfoMessage,
    getInvoice,
    generateInvoice,
    downloadInvoice,
    clearInfoMessage,
  } = useInvoice();

  useEffect(() => {
    const params: any = { offset: 0 };
    if (statusFilter !== 'all') params.status_filter = statusFilter;
    if (source) params.source = source;
    loadOrders(params);
  }, [loadOrders, statusFilter, source]);

  const handleRefresh = () => {
    const params: any = { offset: 0 };
    if (statusFilter !== 'all') params.status_filter = statusFilter;
    if (source) params.source = source;
    loadOrders(params);
  };

  const handleViewOrder = async (orderId: string) => {
    await loadOrderById(orderId);
    setOrderDetailsOpen(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsOpen(false);
  };

  const handleGenerateInvoice = async (orderId: string) => {
    try {
      const result = await generateInvoice(orderId);
      if (result.success && result.download_url) {
        // Optionally auto-download after generation
        // window.open(result.download_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to generate invoice:', error);
    }
  };

  const handleDownloadInvoice = async (orderId: string) => {
    try {
      const invoiceResult = await getInvoice(orderId);
      if (invoiceResult.success && invoiceResult.invoice_id) {
        await downloadInvoice(invoiceResult.invoice_id);
      } else if (invoiceResult.success === false && invoiceResult.message?.includes('not generated')) {
        // Invoice not generated yet, offer to generate
        await handleGenerateInvoice(orderId);
      }
    } catch (error) {
      console.error('Failed to download invoice:', error);
    }
  };

  const isInvoiceAvailable = (order: Order) => {
    return order.status === 'INVOICED' || order.status === 'CLOSED';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          {t('orders.myOrders')}
        </Typography>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={isLoading}
        >
          {t('common.refresh')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {invoiceInfoMessage && (
        <Alert severity="info" sx={{ mb: 3 }} onClose={clearInfoMessage}>
          {invoiceInfoMessage}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('orders.filterByStatus')}</InputLabel>
                <Select
                  value={statusFilter}
                  label={t('orders.filterByStatus')}
                  onChange={(e) => setStatusFilter(e.target.value as Order['status'] | 'all')}
                >
                  <MenuItem value="all">{t('orders.allOrders')}</MenuItem>
                  <MenuItem value="DRAFT">{t('orders.draft')}</MenuItem>
                  <MenuItem value="SUBMITTED">{t('orders.submitted')}</MenuItem>
                  <MenuItem value="CONFIRMED">{t('orders.confirmed')}</MenuItem>
                  <MenuItem value="SHIPPED">{t('orders.shipped')}</MenuItem>
                  <MenuItem value="DELIVERED">{t('orders.delivered')}</MenuItem>
                  <MenuItem value="INVOICED">{t('orders.invoiced')}</MenuItem>
                  <MenuItem value="CLOSED">{t('orders.closed')}</MenuItem>
                  <MenuItem value="CANCELLED">{t('orders.cancelled')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Typography variant="body2" color="text.secondary">
                Total Orders: {total}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {isLoading && orders.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : orders.length === 0 ? (
        <Card>
          <CardContent>
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('orders.noOrders')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <List>
              {orders.map((order, index) => (
                <React.Fragment key={order.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            Order #{order.order_number}
                          </Typography>
                          <Chip
                            label={order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                            color={getStatusColor(order.status)}
                            size="small"
                          />
                          {order.created_via === 'epicor' && (
                            <Chip
                              label={t('orders.offlineBadge')}
                              color="default"
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {order.item_count} items •{' '}
                            {formatIDR(
                              order.grand_total_amount ??
                                computeBreakdown(
                                  order.original_total_amount ?? order.total_amount,
                                  order.total_discount_amount ?? 0,
                                ).total,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Ordered on {formatDate(order.created_at)}
                          </Typography>
                          {order.shipping_date && (
                            <Typography variant="body2" color="text.secondary">
                              Shipped on {formatDate(order.shipping_date)}
                            </Typography>
                          )}
                          {order.invoice_date && (
                            <Typography variant="body2" color="text.secondary">
                              Invoiced on {formatDate(order.invoice_date)}
                            </Typography>
                          )}
                          {order.epicor_order_num && (
                            <Typography variant="body2" color="text.secondary">
                              Epicor Order: #{order.epicor_order_num}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box sx={{ ml: 2, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewIcon />}
                        onClick={() => handleViewOrder(order.id)}
                      >
                        {t('orders.viewDetails')}
                      </Button>
                      {isInvoiceAvailable(order) && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="success"
                          startIcon={<ReceiptIcon />}
                          onClick={() => handleDownloadInvoice(order.id)}
                          disabled={invoiceLoading || isGenerating}
                        >
                          {isGenerating ? 'Generating...' : 'Invoice'}
                        </Button>
                      )}
                      {/* Reorder hidden for offline orders — the backend
                          also refuses with 403 (OFFLINE_ORDER_READONLY).
                          Per stakeholder requirement customers contact
                          their sales rep to place a similar order. */}
                      {order.created_via !== 'epicor' && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<ReorderIcon />}
                          onClick={() => setReorderTarget(order)}
                        >
                          {t('orders.reorder')}
                        </Button>
                      )}
                    </Box>
                  </ListItem>
                  {index < orders.length - 1 && <Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}
                </React.Fragment>
              ))}
            </List>
          </Card>

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={loadMoreOrders}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={20} /> : 'Load More Orders'}
              </Button>
            </Box>
          )}
        </>
      )}

      {/* Order Details Dialog */}
      <Dialog
        open={orderDetailsOpen}
        onClose={handleCloseOrderDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentOrder && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <span>Order Details #{currentOrder.order_number}</span>
              {currentOrder.created_via === 'epicor' && (
                <Chip
                  label={t('orders.offlineBadge')}
                  color="default"
                  variant="outlined"
                  size="small"
                />
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {currentOrder && (
            <Box>
              {currentOrder.created_via === 'epicor' && (
                <Alert severity="info" sx={{ mb: 3 }} icon={false}>
                  <Typography variant="body2" sx={{ fontWeight: 'medium', mb: 0.5 }}>
                    {t('orders.offlineDetail.title')}
                  </Typography>
                  <Typography variant="body2" sx={{ mb: currentOrder.sales_rep_name ? 1.5 : 0 }}>
                    {t('orders.offlineDetail.noteHidden')}
                  </Typography>
                  {currentOrder.sales_rep_name && (
                    <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}
                      >
                        {t('orders.offlineDetail.salesRep')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {currentOrder.sales_rep_name}
                      </Typography>
                      {currentOrder.sales_rep_code && (
                        <Typography variant="caption" color="text.secondary">
                          {t('orders.offlineDetail.salesRepCode', 'Code')}: {currentOrder.sales_rep_code}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Alert>
              )}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('orders.status')}
                  </Typography>
                  <Chip
                    label={currentOrder.status.charAt(0).toUpperCase() + currentOrder.status.slice(1)}
                    color={getStatusColor(currentOrder.status)}
                    sx={{ mt: 0.5 }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('orders.orderDate')}
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(currentOrder.created_at)}
                  </Typography>
                </Grid>
                {currentOrder.shipping_date && (
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('orders.shippingDate')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(currentOrder.shipping_date)}
                    </Typography>
                  </Grid>
                )}
                {currentOrder.invoice_date && (
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('orders.invoiceDate')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(currentOrder.invoice_date)}
                    </Typography>
                  </Grid>
                )}
                {currentOrder.epicor_order_num && (
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('orders.epicorOrderNumber')}
                    </Typography>
                    <Typography variant="body1">
                      #{currentOrder.epicor_order_num}
                    </Typography>
                  </Grid>
                )}
                {currentOrder.epicor_order_status && (
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('orders.epicorStatus')}
                    </Typography>
                    <Typography variant="body1">
                      {currentOrder.epicor_order_status}
                    </Typography>
                  </Grid>
                )}
                {currentOrder.notes && (
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('orders.notes')}
                    </Typography>
                    <Typography variant="body1">
                      {currentOrder.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>

              <Typography variant="h6" sx={{ mb: 2 }}>
                {t('orders.orderItems')}
              </Typography>
              {(() => {
                // 4-line summary — shared between the mobile card layout
                // and the desktop table so both render identical totals.
                // Price list is VAT-inclusive so the PPN line is
                // intentionally omitted — total equals subtotal. DMS
                // invoice still has the faktur-pajak breakdown.
                const original =
                  currentOrder.original_total_amount ?? currentOrder.total_amount;
                const discount = currentOrder.total_discount_amount ?? 0;
                const fb = computeBreakdown(original, discount);
                const subtotal = currentOrder.subtotal_amount ?? fb.subtotal;
                const total = subtotal;
                const summaryRows: Array<{
                  label: string;
                  value: string;
                  bold?: boolean;
                  color?: string;
                }> = [
                  { label: 'Total Selling Price', value: formatIDR(original) },
                  {
                    label: 'Discount (Rp)',
                    value:
                      discount > 0 ? `- ${formatIDR(discount)}` : formatIDR(0),
                    color: discount > 0 ? 'success.main' : undefined,
                  },
                  { label: 'Subtotal', value: formatIDR(subtotal) },
                  { label: 'Total', value: formatIDR(total), bold: true },
                ];

                if (isMobile) {
                  return (
                    <Paper variant="outlined" sx={{ p: 1.5 }}>
                      <Stack spacing={1.5} divider={<Divider flexItem />}>
                        {currentOrder.items.map((item) => (
                          <Box key={item.id}>
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {item.product_name}
                            </Typography>
                            {item.epicor_part_number && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mt: 0.25 }}
                              >
                                {item.epicor_part_number}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                mt: 0.75,
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {item.quantity} × {formatIDR(item.unit_price)}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {formatIDR(item.total_price)}
                              </Typography>
                            </Box>
                          </Box>
                        ))}

                        {/* Totals summary — same content as desktop, stacked. */}
                        <Stack spacing={0.5}>
                          {summaryRows.map((r, i) => (
                            <Box
                              key={`summary-${i}`}
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'baseline',
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: r.bold ? 'bold' : 'normal', color: r.color }}
                              >
                                {r.label}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: r.bold ? 'bold' : 'normal', color: r.color }}
                              >
                                {r.value}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </Stack>
                    </Paper>
                  );
                }

                return (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('cart.item')}</TableCell>
                          <TableCell>{t('products.description')}</TableCell>
                          <TableCell align="right">{t('cart.quantity')}</TableCell>
                          <TableCell align="right">{t('orders.unitPrice')}</TableCell>
                          <TableCell align="right">{t('cart.total')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {currentOrder.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                {item.product_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {item.epicor_part_number || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">{formatIDR(item.unit_price)}</TableCell>
                            <TableCell align="right">{formatIDR(item.total_price)}</TableCell>
                          </TableRow>
                        ))}
                        {summaryRows.map((r, i) => (
                          <TableRow key={`summary-${i}`}>
                            <TableCell
                              colSpan={4}
                              align="right"
                              sx={{
                                fontWeight: r.bold ? 'bold' : 'normal',
                                borderBottom: i === summaryRows.length - 1 ? undefined : 0,
                                py: 0.75,
                                color: r.color,
                              }}
                            >
                              {r.label}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{
                                fontWeight: r.bold ? 'bold' : 'normal',
                                borderBottom: i === summaryRows.length - 1 ? undefined : 0,
                                py: 0.75,
                                color: r.color,
                              }}
                            >
                              {r.value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {currentOrder && isInvoiceAvailable(currentOrder) && (
            <Button
              startIcon={<ReceiptIcon />}
              onClick={() => handleDownloadInvoice(currentOrder.id)}
              disabled={invoiceLoading || isGenerating}
              color="success"
            >
              {isGenerating ? 'Generating Invoice...' : 'Download Invoice'}
            </Button>
          )}
          {currentOrder && (
            <Button
              variant="contained"
              startIcon={<ReorderIcon />}
              onClick={() => {
                setReorderTarget(currentOrder);
                setOrderDetailsOpen(false);
              }}
            >
              {t('orders.reorder')}
            </Button>
          )}
          <Button onClick={handleCloseOrderDetails}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Reorder ("Buy Again") confirmation */}
      <ReorderDialog
        open={!!reorderTarget}
        order={reorderTarget}
        cartItemCount={cartItemCount}
        onClose={() => setReorderTarget(null)}
        onSuccess={async (msg) => {
          setReorderTarget(null);
          await loadCart();
          setReorderToast(msg);
          // Give the user a moment to read the toast, then route to the cart.
          setTimeout(() => navigate('/cart'), 600);
        }}
      />
      <Snackbar
        open={!!reorderToast}
        autoHideDuration={4000}
        onClose={() => setReorderToast('')}
        message={reorderToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};